import React, {ChangeEvent, useRef, useState, useEffect, useTransition} from 'react'
import {Col, Row} from 'react-bootstrap'
import ReportList from './ReportList'
import Chart from 'react-google-charts'
import AppButton from '../../../components/AppButton'
import Filters from './Filters'
import {OptionObjInterface} from '../../../components/AppSelect'
import {
  convertTableMapDataToTableDataType,
  reportListTypeFromServer,
  scheduledMissedTotalCountType,
  scheduledReportListType,
  scheduledReportType,
  storeTableDataMapValue,
} from './model'
import {useConvertToPdf} from '../../../hooks/useConvertToPdf'
import {getScheduledGraphData, getScheduledReports, uploadScheduledReport} from './service'
import {extractDateFromReportListItem, formatScheduleReport, mapFields} from './formatList'
import {AppToast} from '../../../Utils/AppToast'
import {
  convertTableMapDataToTable,
  getChartData,
  getClientsAndStoreSites,
  storeTableDataAfterReportsSelect,
  storeTableDataAfterReportUpload,
} from './utils'
import TableWrapper from './tableWrapper'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {fontStyle} from 'html2canvas/dist/types/css/property-descriptors/font-style'

const acceptedExcelFileString = '.xlsx'

type ScheduledReportProps = {
  isScheduledTab: boolean
}

const ScheduledReport = ({isScheduledTab}: ScheduledReportProps) => {
  const {toast} = AppToast

  const [exportToPdfLoading, setExportToPdfLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const reportRef = useRef<HTMLDivElement>(null)

  const [reportFetchLoading, setReportFetchLoading] = useState(false)
  const [reportUploadLoading, setReportUploadLoading] = useState(false)

  const [clientSiteMap, setClientSiteMap] = useState(
    new Map<string, Map<string, scheduledMissedTotalCountType>>([])
  )
  const [selectedClient, setSelectedClient] = useState('')
  const [selectedSite, setSelectedSite] = useState('')

  const clientDropdownOptions: OptionObjInterface[] =
    clientSiteMap.size === 0
      ? []
      : Array.from(clientSiteMap.keys())?.map((key) => ({label: key, value: key}))

  const selectedClientMapData = clientSiteMap.get(selectedClient)

  const siteDropdownOptions: OptionObjInterface[] =
    !selectedClient || !clientSiteMap.get(selectedClient)
      ? []
      : Array.from(selectedClientMapData?.keys() || []).map((site) => ({
          label: site,
          value: site,
        }))

  const [scheduledReports, setScheduledReports] = useState<scheduledReportListType[]>(() => [])
  const [tableDataMap, setTableDataMap] = useState(new Map<string, storeTableDataMapValue>())
  const [shifts, setShifts] = useState(new Set<string>())

  const [missedScheduledCount, setMissedScheduledCount] = useState<{
    missed: number
    scheduled: number
    total: number
  }>({missed: 0, scheduled: 0, total: 0})

  const {missed, scheduled, total} = missedScheduledCount

  const [scheduledReportsSelected, setScheduledReportsSelected] = useState<
    Map<string, scheduledReportListType>
  >(new Map())

  const resetData = () => {
    setMissedScheduledCount({missed: 0, scheduled: 0, total: 0})
    setShifts(new Set([]))
    setClientSiteMap(new Map([]))
    setTableDataMap(new Map([]))
  }

  const handleGetScheduledReportClick = async () => {
    const reportIds = Array.from(scheduledReportsSelected.keys())?.map((id) => Number(id))
    try {
      setReportFetchLoading(true)
      resetData()
      const {data} = await getScheduledGraphData(reportIds)
      const success = data.success
      if (!success) {
        toast.error('Unable to read the scheduled reports')
        return
      }

      const scheduleReportData = data?.data
      const {missed, scheduled, totalCount} = getChartData(scheduleReportData)
      const clientSiteMap = getClientsAndStoreSites(scheduleReportData)
      const {tableDataMap, shiftSet} = storeTableDataAfterReportsSelect(data.data)
      setShifts(shiftSet)
      setTableDataMap(tableDataMap)
      setClientSiteMap(clientSiteMap)
      setMissedScheduledCount({missed, total: totalCount, scheduled})
      toast.success('Scheduled Reports read successfully')

      toast.success('Read the reports successfullly')
    } catch (error) {
      console.error('get graph data error', error)
      toast.error('Cannot read excel data')
    } finally {
      setReportFetchLoading(false)
    }
  }

  const getListReportStyle = (isSelected: boolean): React.CSSProperties => {
    return {
      cursor: 'pointer',
      backgroundColor: isSelected ? '#007bff' : '',
      color: isSelected ? 'white' : '',
    }
  }

  const getScheduledReportStyle = (report: scheduledReportListType) => {
    return getListReportStyle(isScheduledReportSelected(report))
  }

  const isScheduledReportSelected = (report: scheduledReportListType) => {
    const reportId = report['id']
    return !!scheduledReportsSelected.has(reportId)
  }

  const updateTheSelectedScheduledReport = (
    report: scheduledReportListType,
    setSet: React.Dispatch<React.SetStateAction<Map<string, scheduledReportListType>>>
  ) => {
    const {id, date, fileName, label} = report
    setSet((prev) => {
      const newMap = new Map(prev)
      if (newMap.has(id)) {
        newMap.delete(id)
      } else {
        newMap.set(id, {date, fileName, id, label})
      }
      return newMap
    })
  }

  const handleScheduledReportClick = (report: scheduledReportListType) => {
    updateTheSelectedScheduledReport(report, setScheduledReportsSelected)
  }

  const scheduledReportData = [
    ['Report', 'Value'],
    ['Scheduled', scheduled],
    ['Missed', missed],
  ]

  const handleClientChange = (client: string) => {
    setSelectedClient(client)
    setSelectedSite('')
  }

  const handleSiteChange = (site: string) => {
    setSelectedSite(site)
    const counts = selectedClientMapData?.get(site) as scheduledMissedTotalCountType
    const {missed, scheduled, total} = counts
    setMissedScheduledCount({missed, scheduled, total})
  }

  const setTheUploadedFileAsSelectedReport = (report: reportListTypeFromServer) => {
    const {id, filename, upload_date} = report
    setScheduledReports((prev) => [{...mapFields(report, 1)}, ...prev])
    const map = new Map<string, scheduledReportListType>()
    map.set(id, {date: upload_date, fileName: filename, id, label: ''})
    setScheduledReportsSelected(map)
  }

  //file upload btn click, opens the file dialog
  const handleFileInputClick = () => {
    if (!fileInputRef?.current?.click()) return
    fileInputRef.current.click()
  }

  //handle file change is called by getFile internally
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return null
    var file = event?.target?.files[0]
    event.target.value = ''
    return file
  }

  const uploadFile = async (file: File) => {
    try {
      setReportUploadLoading(true)
      resetData()
      setScheduledReportsSelected(new Map([]))
      const response = await uploadScheduledReport(file)
      const {data} = response
      const success = data.success
      if (!success) {
        const defaultMsg = 'Unable to upload the scheduled report'
        const responseMsg = data?.message
        toast.error(responseMsg ?? defaultMsg)
        return
      }
      const reportFileData = data?.data?.uploadedReport
      const scheduleReportData = data?.data?.excelData?.data
      const {missed, scheduled, totalCount} = getChartData(scheduleReportData)
      const clientSiteMap = getClientsAndStoreSites(scheduleReportData)
      const shiftName = data?.data?.excelData?.shiftname
      const {tableDataMap, shiftSet} = storeTableDataAfterReportUpload(
        scheduleReportData,
        shiftName
      )
      setShifts(shiftSet)
      setTableDataMap(tableDataMap)
      setClientSiteMap(clientSiteMap)
      setMissedScheduledCount({missed, total: totalCount, scheduled})
      setTheUploadedFileAsSelectedReport(reportFileData)
      toast.success('Scheduled Report uploaded successfully')
    } catch (error) {
      console.error('schedule report upload error', error)
      toast.error('Something went wrong, Please try after some time')
    } finally {
      setReportUploadLoading(false)
    }
  }

  const getFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = handleFileChange(event)
    if (!file) return
    uploadFile(file)
  }

  const handleExcelUploadBtnClick = () => {
    if (fileInputRef.current) fileInputRef.current.accept = acceptedExcelFileString
    handleFileInputClick()
  }

  const fetchScheduledReports = async () => {
    try {
      setReportFetchLoading(true)
      const {data} = await getScheduledReports()
      if (data.success && data.data && data.data?.length > 0) {
        const reportData = data.data
        setScheduledReports(formatScheduleReport(reportData))
        toast.success('Scheduled Reports found')
      } else {
        toast.error('Sorry, no scheduled reports found')
        setScheduledReports([])
      }
    } catch (error) {
      console.error('fetch scheduled reports error', error)
      toast.error('Unable to get Scheduled Reports')
    } finally {
      setReportFetchLoading(false)
    }
  }

  const getHeadingInPdfExport = () => {
    const textArray = []

    const clientText = `Client : ${selectedClient}`
    const siteText = `Site : ${selectedSite}`
    if (selectedClient) textArray.push({text: clientText, yPosition: 10, fontSize: 13})
    if (selectedSite) textArray.push({text: siteText, yPosition: 10, fontSize: 13})

    textArray.push({
      text: 'Reports selected',
      yPosition: 10,
      fontSize: 13,
    })

    const arrayOfSelectedReports = Array.from(scheduledReportsSelected.values())
    arrayOfSelectedReports.forEach((report, index) => {
      const {date, fileName} = report
      const number = index + 1
      const formattedDate = extractDateFromReportListItem(date)
      textArray.push({
        text: `${number}-${fileName} - Date - ${formattedDate}`,
        yPosition: 8,
        fontSize: 13,
      })
    })
    return textArray
  }

  const exportChartAndTableToPdf = async () => {
    try {
      if (!reportRef?.current) return
      setExportToPdfLoading(true)
      const pdfFileName = 'Scheduled Report'
      const pageWidth = 210 // A4 width in mm
      const centerPosition = Math.floor(pageWidth / 2)
      const margin = 10 // Margin on each side
      const pdf = new jsPDF('p', 'mm', 'a4')
      const headingArray = getHeadingInPdfExport()
      pdf.setFontSize(15)
      pdf.setFont('helvetica', 'bold') // Set the font to bold
      pdf.text('Scheduled Report', centerPosition, 10, {align: 'center'})
      let yPositionCurrent = 20

      headingArray?.forEach(({text, yPosition, fontSize}) => {
        yPositionCurrent += yPosition
        pdf.setFontSize(fontSize)
        pdf.setFont('helvetica', 'normal') // Reset font to normal
        pdf.text(text, 20, yPositionCurrent)
      })
      const pieChartCanvasImage = await html2canvas(reportRef?.current, {scrollY: 0})
      const pieChartImageData = pieChartCanvasImage.toDataURL('image/png')

      const scaleFactor = 1.3 // Scale factor for enlarging the chart

      let imgWidth = Math.floor((pageWidth - margin) * scaleFactor) // Increase width
      let imgHeight = (pieChartCanvasImage.height * imgWidth) / pieChartCanvasImage.width // Maintain aspect ratio

      pdf.addImage(pieChartImageData, 'PNG', margin, yPositionCurrent + 20, imgWidth, imgHeight)

      // Define the table headers and data
      const tableHeaders = [
        {header: 'Shift', dataKey: 'shift'},
        {header: 'Checkpoints', dataKey: 'checkpoints'},
        {header: 'Expected Scans', dataKey: 'expectedScans'},
        {header: 'No. of Successful Scans', dataKey: 'successfulScans'},
        {header: 'No. of Missed Scans', dataKey: 'missedScans'},
        {header: 'Success Scan %', dataKey: 'successScanPercentage'},
      ]

      const tableData = convertTableMapDataToTable({
        shifts,
        tableDataMap,
        selectedClient,
        selectedSite,
      })

      autoTable(pdf, {
        columns: tableHeaders,
        body: tableData,
        startY: yPositionCurrent + imgHeight + 20,
        theme: 'grid', // Optional themes: 'striped', 'plain', 'grid'
        headStyles: {
          fillColor: [63, 81, 181], // Custom color for header background
          textColor: 255, // White text color
          fontSize: 12,
          fontStyle: 'bold',
          halign: 'center', // Center-align header text
        },
        bodyStyles: {
          halign: 'center', // Center-align body text
          fontSize: 10,
        },
        columnStyles: {
          shift: {cellWidth: 20},
          successfulScans: {fillColor: [0, 128, 0], textColor: 255, halign: 'center'},
          missedScans: {fillColor: [255, 0, 0], textColor: 255, halign: 'center'},
          // Adjust width of 'Shift' column
          // Set widths for other columns if needed
        },
      })
      pdf.save(pdfFileName)
    } catch (error) {
      console.error('scheduled report pdf file generate error', error)
      toast.error('Something went wrong, could not generate pdf')
    } finally {
      setExportToPdfLoading(false)
    }
  }

  useEffect(() => {
    if (!isScheduledTab) return
    fetchScheduledReports()
  }, [isScheduledTab])

  return (
    <>
      <Row>
        <Col md={3}>
          <>
            <ReportList
              getScheduledReportStyle={getScheduledReportStyle}
              handleExcelUploadBtnClick={handleExcelUploadBtnClick}
              handleGetScheduledReportClick={handleGetScheduledReportClick}
              handleScheduledReportClick={handleScheduledReportClick}
              isScheduledReportSelected={isScheduledReportSelected}
              scheduledReports={scheduledReports}
              reportFetchLoading={reportFetchLoading}
              reportUploadLoading={reportUploadLoading}
            />
          </>
        </Col>
        <Col md={9}>
          <Filters
            selectedClient={selectedClient}
            selectedSite={selectedSite}
            clientDropdownOptions={clientDropdownOptions}
            handleClientChange={handleClientChange}
            handleSiteChange={handleSiteChange}
            siteDropdownOptions={siteDropdownOptions}
          />
          {total === 0 ? null : (
            <div className='d-flex justify-content-end align-items-center my-3'>
              <AppButton
                label='Download Scheduled Report'
                onClick={() => exportChartAndTableToPdf()}
                btnSize='sm'
                icon={'bi bi-download'}
                loading={exportToPdfLoading}
                disabled={exportToPdfLoading}
              />
            </div>
          )}
          <div>
            {total === 0 ? (
              <p className='h3 text-center'>No Report selected</p>
            ) : (
              <div ref={reportRef}>
                <Chart
                  width={'100%'}
                  height={'250px'}
                  chartType='PieChart'
                  loader={<div>Loading Chart...</div>}
                  data={scheduledReportData}
                  options={{
                    title: 'Scheduled Report Data',
                    hAxis: {title: 'Event Type', minValue: 0},
                    colors: ['#01AF4E', '#BE0201'],
                  }}
                />
              </div>
            )}
          </div>
          <TableWrapper
            tableDataMap={tableDataMap}
            selectedClient={selectedClient}
            selectedSite={selectedSite}
            shifts={shifts}
          />
        </Col>
      </Row>
      <input type='file' ref={fileInputRef} className={`d-none`} onChange={getFile} />
    </>
  )
}

export default ScheduledReport
